import React, { useState } from 'react';
import { initiateRefund } from '../services/adminService';
import { tableSearch } from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from './Toast';

interface RefundModalProps {
  booking: any | null;
  onClose: () => void;
  onSuccess: () => void;
}

const RefundModal: React.FC<RefundModalProps> = ({ booking, onClose, onSuccess }) => {
  const [refundType, setRefundType] = useState('FULL');
  const [customPercentage, setCustomPercentage] = useState<number | ''>('');
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const { toast, showToast } = useToast();

  if (!booking) return null;

  const handleSubmit = async () => {
    if (refundType === 'CUSTOM' && (customPercentage === '' || customPercentage <= 0 || customPercentage > 100)) {
      showToast('Please enter a valid percentage between 1 and 100', 'error');
      return;
    }
    
    setProcessing(true);
    try {
      await initiateRefund({
        bookingId: booking.id,
        refundType: refundType,
        customPercentage: refundType === 'BASE_TRAVEL_CUSTOM' ? Number(customPercentage) : undefined,
        reason
      });
      showToast('Refund initiated successfully!', 'success');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to initiate refund', 'error');
      setProcessing(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10001,
      background: "rgba(15,23,42,0.6)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16
    }} onClick={onClose}>
      <Toast toast={toast} />
      <div style={{
        background: "hsl(var(--card))", borderRadius: 20, padding: "36px 40px",
        width: 440, maxWidth: "92vw", boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
        border: "1px solid hsl(var(--border))", animation: "bdmSlideUp .25s ease"
      }} onClick={(e) => e.stopPropagation()}>
        
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#fef2f2,#fee2e2)",
            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 28, boxShadow: "0 0 0 8px #fef2f2"
          }}>
            ↩
          </div>
        </div>
        
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "hsl(var(--foreground))", textAlign: "center" }}>
          Initiate Refund
        </h2>
        <p style={{ margin: "10px 0 24px", fontSize: 14, color: "#64748b", textAlign: "center", lineHeight: 1.6 }}>
          Select the refund tier for booking <span style={{fontWeight: 700}}>#{booking.id?.slice(0,8)}</span>.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Refund Tier</label>
            <select
              style={{ ...tableSearch, width: "100%", boxSizing: "border-box" }}
              value={refundType}
              onChange={(e) => setRefundType(e.target.value)}
            >
              <option value="FULL">Complete Refund (100%)</option>
              <option value="BASE_TRAVEL">Base + Travel Cost</option>
              <option value="BASE_TRAVEL_MINUS_10">Base + Travel Cost - 10%</option>
              <option value="BASE_TRAVEL_CUSTOM">Custom Percentage</option>
            </select>
          </div>

          {refundType === 'BASE_TRAVEL_CUSTOM' && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Custom Percentage (%)</label>
              <input
                type="number"
                min="1" max="100"
                placeholder="e.g. 50"
                style={{ ...tableSearch, width: "100%", boxSizing: "border-box" }}
                value={customPercentage}
                onChange={(e) => setCustomPercentage(e.target.value ? Number(e.target.value) : '')}
              />
            </div>
          )}

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>Reason (Optional)</label>
            <input
              type="text"
              placeholder="Enter reason..."
              style={{ ...tableSearch, width: "100%", boxSizing: "border-box" }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={onClose}
            disabled={processing}
            style={{
              flex: 1, padding: "12px 0", borderRadius: 12, border: "1.5px solid hsl(var(--border))",
              background: "transparent", color: "hsl(var(--muted-foreground))", fontWeight: 700, fontSize: 14,
              cursor: processing ? "not-allowed" : "pointer"
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={processing}
            style={{
              flex: 1, padding: "12px 0", borderRadius: 12, border: "none",
              background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", fontWeight: 700, fontSize: 14,
              cursor: processing ? "not-allowed" : "pointer", opacity: processing ? 0.8 : 1,
              boxShadow: "0 4px 12px rgba(239,68,68,0.3)"
            }}
          >
            {processing ? "Processing..." : "Confirm Refund"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundModal;
